<template>
    <div class="tw-px-4">
        <div class="tw-grid tw-grid-cols-12 tw-gap-2">
            <div class="tw-col-span-12 md:tw-col-span-3 lg:tw-col-span-2">
                <div
                    class="tw-p-4 tw-min-h-fullx tw-bg-white tw-rounded-lg tw-border tw-border-solid tw-border-gray-200 tw-flex tw-flex-col tw-gap-2">
                    <button v-for="type in typeOptions" :key="type.id" @click="selectedTab = type.id"
                        :class="{ 'tw-bg-primary-50 tw-text-primary-700 tw-border-primary-500 hover:tw-text-primary-700 hover:tw-bg-primary-50': selectedTab === type.id }"
                        class="tw-flex tw-items-center tw-gap-2 tw-text-gray-500 tw-text-sm tw-font-medium tw-p-3 tw-rounded-md tw-border tw-border-solid tw-border-gray-200 hover:tw-bg-gray-50 hover:tw-text-gray-700 tw-duration-200">
                        <icon :icon="type.icon" class="tw-text-lg" />
                        <span>{{ type.name }}</span>
                    </button>
                </div>
            </div>
            <div class="tw-col-span-12 md:tw-col-span-9 lg:tw-col-span-10">
                <div>

                    <div class="tw-flex tw-items-center tw-justify-between tw-mb-2">
                        <div class="tw-flex tw-items-center tw-gap-2">
                            <FormField v-model="search" type="text" :hide-label="true" :hide-errors="true"
                                :placeholder="$t('common.categories-search-placeholder')"
                                left-icon="solar:magnifer-linear" @input="onSearch"
                                class="tw-bg-white tw-rounded-md tw-min-w-[300px]" />

                            <!-- Type Filter -->
                            <FormSelect v-if="false" v-model="filters.type" :options="typeOptions"
                                :placeholder="$t('common.filter-by-type', 'Filter by type')"
                                left-icon="solar:filter-bold-duotone"
                                class="tw-bg-white tw-rounded-md tw-min-w-[150px]" />

                        </div>

                        <div>
                            <a-button @click="showCreateModal = true" intent="flat"
                                left-icon="solar:add-circle-bold-duotone" class="tw-min-w-[160px]">
                                {{ $t('common.create-new-category') }}
                            </a-button>
                        </div>
                    </div>

                    <!-- Table Section -->

                    <CategoryTable :loading="loading" :items="categories" :options="paginationOptions" @next="onNext"
                        @previous="onPrevious" @edit="onEdit" @delete="onDelete" />

                    <!-- Create Category Modal -->
                    <CategoryCreateModal v-if="showCreateModal" :types="typeOptions" :type="selectedTab" :visible="showCreateModal"
                        @close="showCreateModal = false" @created="onCategoryCreated" />

                    <!-- Edit Category Modal -->
                    <CategoryEditModal v-if="showEditModal" :visible="showEditModal" :types="typeOptions"  :category="selectedCategory"
                        @close="showEditModal = false" @updated="onCategoryUpdated" />
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, reactive, onMounted, watch, provide } from 'vue';
import { useI18n } from 'vue-i18n';
import FormField from '@/components/form/FormField.vue';
import FormSelect from '@/components/form/FormSelect.vue';
import CategoryTable from '../categories/table/CategoryTable.vue';
import CategoryCreateModal from '../categories/modals/CategoryCreateModal.vue';
import CategoryEditModal from '../categories/modals/CategoryEditModal.vue';
import categoriesApi from '@/api/categories';
import useAlert from '@/composables/useAlert';

const { t } = useI18n();
const selectedTab = ref('product');


// Reactive data
const loading = ref(false);

const categories = ref([]);
const search = ref('');
const showCreateModal = ref(false);
const showEditModal = ref(false);
const selectedCategory = ref(null);

// Pagination options
const paginationOptions = ref({
    current_page: 1,
    per_page: 10,
    total: 0,
    last_page: 1,
    next_page_url: null,
    prev_page_url: null
});

// Filters
const filters = reactive({
    type: selectedTab.value
});

// Type options for filter
const typeOptions = ref([
    { id: 'product', name: t('common.product'), icon: 'solar:box-bold-duotone' },
    { id: 'service', name: t('common.service'), icon: 'solar:settings-bold-duotone' },
    { id: 'company', name: t('common.company'), icon: 'solar:buildings-bold-duotone' },
    { id: 'type', name: t('common.type'), icon: 'solar:widget-bold-duotone' },
    { id: 'topic', name: t('common.topic'), icon: 'solar:bookmark-bold-duotone' },
    { id: 'language', name: t('common.language'), icon: 'solar:translation-bold-duotone' }
]);
provide('typeOptions', typeOptions);

// Search debounce
let searchTimeout = null;

// Watch for filter changes
watch(() => filters.type, () => {
    fetchCategories(1);
});

watch(() => selectedTab.value, () => {
    filters.type = selectedTab.value;
});

// Methods
const fetchCategories = async (page = 1) => {
    loading.value = true;

    try {
        const params = {
            page,
            per_page: paginationOptions.value.per_page,
            search: search.value,
            ...filters
        };

        const response = await categoriesApi.admin.paginate(params);

        if (response.data && response.data.data) {
            categories.value = response.data.data;
            paginationOptions.value = {
                current_page: response.data.current_page,
                per_page: response.data.per_page,
                total: response.data.total,
                last_page: response.data.last_page,
                next_page_url: response.data.next_page_url,
                prev_page_url: response.data.prev_page_url
            };
        }
    } catch (error) {
        console.error('Error fetching categories:', error);
        useAlert(t('errors.something-went-wrong', 'Something went wrong'), 'danger');
    } finally {
        loading.value = false;
    }
};

const onSearch = () => {
    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => {
        fetchCategories(1);
    }, 500);
};

const onNext = () => {
    if (paginationOptions.value.current_page < paginationOptions.value.last_page) {
        fetchCategories(paginationOptions.value.current_page + 1);
    }
};

const onPrevious = () => {
    if (paginationOptions.value.current_page > 1) {
        fetchCategories(paginationOptions.value.current_page - 1);
    }
};

const onCategoryCreated = (category) => {
    showCreateModal.value = false;
    categories.value.unshift(category);
    useAlert(t('messages.item-created'), 'success');
};

const onCategoryUpdated = (category) => {
    showEditModal.value = false;
    selectedCategory.value = null;

    categories.value = categories.value.map(c => c.id === category.id ? category : c);
    useAlert(t('messages.changes-saved'), 'success');
};

const onEdit = (category) => {
    selectedCategory.value = category;
    showEditModal.value = true;
};

const onDelete = () => {
    // The delete functionality is handled in CategoryTableActions component
    // This is just for any additional logic if needed
    fetchCategories(paginationOptions.value.current_page);
};

// Initialize
onMounted(() => {
    fetchCategories();
});
</script>

<style scoped>
/* Add any custom styles here */
</style>